import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import LegalAcceptanceForm from "./LegalAcceptanceForm";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  defaultLegalAcceptanceValues,
  isLegalAcceptanceComplete,
} from "../config/legalVersions";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TermsReacceptanceGate({ children }) {
  const { isAuthenticated, token, refreshUser, user } = useAuth();
  const [checking, setChecking] = useState(true);
  const [needsReacceptance, setNeedsReacceptance] = useState(false);
  const [values, setValues] = useState(defaultLegalAcceptanceValues());
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (!isAuthenticated || !token) {
        setChecking(false);
        setNeedsReacceptance(false);
        return;
      }

      if (user?.terms_reacceptance_required) {
        setNeedsReacceptance(true);
        setChecking(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/legal/terms-status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setNeedsReacceptance(Boolean(response.data.needs_reacceptance));
        }
      } catch (error) {
        if (!cancelled) {
          setNeedsReacceptance(false);
        }
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    };

    check();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, user?.terms_reacceptance_required]);

  const handleChange = (field, value) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async () => {
    const nextErrors = {};
    if (!values.acceptedTerms) {
      nextErrors.acceptedTerms = "Devi accettare i Termini di Utilizzo per continuare";
    }
    if (!values.acceptedClauses1341) {
      nextErrors.acceptedClauses1341 = "Devi approvare le clausole ex artt. 1341-1342 c.c.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/legal/accept`,
        {
          acceptedTerms: values.acceptedTerms,
          acceptedClauses1341: values.acceptedClauses1341,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      setNeedsReacceptance(false);
      toast.success("Accettazione contrattuale registrata.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione dell'accettazione");
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return children;
  }

  return (
    <>
      {children}
      <Dialog open={needsReacceptance} onOpenChange={() => {}}>
        <DialogContent className="max-w-2xl" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Aggiornamento Termini di Utilizzo</DialogTitle>
            <DialogDescription>
              I Termini di Utilizzo Affidya sono stati aggiornati. Per continuare a usare la piattaforma
              è necessario leggere e accettare la nuova versione contrattuale.
            </DialogDescription>
          </DialogHeader>

          <LegalAcceptanceForm values={values} onChange={handleChange} errors={errors} compact />

          <Button
            onClick={handleSubmit}
            disabled={submitting || !isLegalAcceptanceComplete(values)}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Registrazione in corso...
              </>
            ) : (
              "Conferma accettazione e continua"
            )}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
